/**
 * Onboarding Provider Component
 *
 * Wrapper that mounts OnboardingContext + MuiTourEngine host for guided tours.
 */

import React, { useEffect, useState, ReactNode } from "react";
import { useAuth } from "../../contexts/auth/authContext";
import { OnboardingProvider as ContextProvider } from "../../contexts/OnboardingContext";
import { OnboardingManager } from "./OnboardingManager";
import { logger } from "../../utils/logger";
import type { OnboardingFlow, OnboardingConfig } from "../../types/onboarding";

/**
 * Onboarding Provider Props
 */
interface OnboardingProviderProps {
  children: ReactNode;
  /** Custom tours configuration */
  tours?: OnboardingFlow[];
  /** Custom configuration */
  config?: Partial<OnboardingConfig>;
  /** Whether to auto-start tours for new users */
  autoStart?: boolean;
}

/**
 * Onboarding Provider Component
 *
 * Wraps the application with onboarding context and handles initialization.
 * This component should be placed after AuthProvider in the component tree.
 */
export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({
  children,
  tours = [],
  config = {},
  autoStart = true,
}) => {
  const { user, isAuthenticated } = useAuth();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user?.id && !initialized) {
      logger.info("OnboardingProvider initialized", {
        userId: user.id,
        userRole: user.role,
        toursCount: tours.length,
      });
      setInitialized(true);
    }
  }, [isAuthenticated, user?.id, user?.role, tours.length, initialized]);

  const envTrackProgress = import.meta.env.VITE_ONBOARDING_TRACK_PROGRESS;
  const defaultTrackProgress =
    envTrackProgress === undefined ? true : envTrackProgress === "true";

  return (
    <ContextProvider
      tours={tours}
      config={{
        ...config,
        autoStart,
        trackProgress: config.trackProgress ?? defaultTrackProgress,
      }}
    >
      {/* Co-located with provider so React Refresh / HMR never runs this before the context exists */}
      <OnboardingManager />
      {children}
    </ContextProvider>
  );
};

export default OnboardingProvider;
